import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider, type FieldErrors, type FieldPath } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/serge'
import { WizardProgress } from '@/components/serge/WizardProgress'
import {
  getSergeLegalEntities,
  createSergeLegalEntity,
  updateSergeLegalEntity,
  deleteSergeLegalEntity,
} from '@/services/sergeApi'
import { legalEntitySchema, type LegalEntityFormData } from '@/lib/legalEntitySchema'
import { GeneralInfoStep, ContactStep, RGPDStep, CGVStep, SoftwareStep } from '@/components/legal-entities/steps'
import {
  Loader2,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowLeft,
} from 'lucide-react'
import toast from 'react-hot-toast'

interface LegalEntity extends LegalEntityFormData {
  id: number
  createdAt?: string
  updatedAt?: string
}

const STEPS = [
  { label: 'Général', optional: false },
  { label: 'Contact', optional: false },
  { label: 'RGPD', optional: true },
  { label: 'CGV', optional: true },
  { label: 'Logiciels', optional: true },
  { label: 'Récapitulatif', optional: false },
]

const STEP_FIELDS: Array<Array<FieldPath<LegalEntityFormData>>> = [
  ['name', 'legalName', 'legalForm', 'capital', 'registrationNumber', 'vatNumber'],
  [
    'address.line1',
    'address.postalCode',
    'address.city',
    'address.country',
    'contactEmail',
    'contactPhone',
    'website',
    'directorName',
    'publicationDirector',
    'hostingProvider.name',
    'hostingProvider.phone',
    'hostingProvider.email',
    'hostingProvider.address',
  ],
  [
    'dataControllerName',
    'dpoEmail',
    'privacyContactEmail',
    'processingPurposes',
    'legalBases',
    'retentionPolicy',
    'subProcessors',
    'transferOutsideEu',
    'securityMeasures',
  ],
  [
    'businessModel',
    'serviceDescription',
    'paymentMethods',
    'latePaymentPenalties',
    'withdrawalPolicy',
    'refundPolicy',
    'mediationInfo',
    'jurisdiction',
  ],
  ['softwareUsed', 'isSubcontractor', 'masterServiceAgreement', 'paymentSchedule'],
]

const collectErrorPaths = (
  errors: FieldErrors<LegalEntityFormData>,
  prefix = ''
): string[] => {
  const paths: string[] = []
  if (!errors) return paths

  Object.entries(errors).forEach(([key, value]) => {
    if (!value) return
    const nextPath = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'object' && ('message' in value || 'type' in value)) {
      paths.push(nextPath)
      return
    }

    if (typeof value === 'object') {
      paths.push(...collectErrorPaths(value as FieldErrors<LegalEntityFormData>, nextPath))
    }
  })

  return paths
}

const findFirstErrorDetail = (
  errors: FieldErrors<LegalEntityFormData>,
  prefix = ''
): { path: string; message?: string } | null => {
  if (!errors) return null

  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue
    const path = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'object' && 'message' in value) {
      return { path, message: (value as { message?: string }).message }
    }

    if (typeof value === 'object') {
      const nested = findFirstErrorDetail(value as FieldErrors<LegalEntityFormData>, path)
      if (nested) return nested
    }
  }

  return null
}

const findFirstErrorStep = (errors: FieldErrors<LegalEntityFormData>): number | null => {
  const errorPaths = collectErrorPaths(errors)
  if (!errorPaths.length) return null

  for (let stepIndex = 0; stepIndex < STEP_FIELDS.length; stepIndex += 1) {
    const fields = STEP_FIELDS[stepIndex]
    const hasError = fields.some((field) =>
      errorPaths.some((path) => path.startsWith(field as string))
    )
    if (hasError) return stepIndex
  }

  return null
}

const emptyEntity: LegalEntityFormData = {
  name: '',
  legalName: '',
  legalForm: '',
  capital: '',
  registrationNumber: '',
  vatNumber: '',
  address: {},
  contactEmail: '',
  contactPhone: '',
  website: '',
  directorName: '',
  publicationDirector: '',
  hostingProvider: {},
  dataControllerName: '',
  dpoEmail: '',
  privacyContactEmail: '',
  processingPurposes: [],
  legalBases: [],
  retentionPolicy: '',
  subProcessors: [],
  transferOutsideEu: false,
  securityMeasures: '',
  businessModel: '',
  serviceDescription: '',
  paymentMethods: [],
  latePaymentPenalties: '',
  withdrawalPolicy: '',
  refundPolicy: '',
  mediationInfo: '',
  jurisdiction: '',
  softwareUsed: [],
  isSubcontractor: false,
  masterServiceAgreement: {},
  paymentSchedule: [],
}

const normalizeNulls = (value: unknown): unknown => {
  if (value === null) return undefined
  if (Array.isArray(value)) return value.map(normalizeNulls)
  if (typeof value === 'object' && value) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, normalizeNulls(val)])
    )
  }
  return value
}

const mergeDefaults = <T extends Record<string, unknown>>(defaults: T, value: Partial<T>): T => {
  const result: Record<string, unknown> = { ...defaults }
  Object.entries(value).forEach(([key, incoming]) => {
    if (incoming === undefined) return
    const current = result[key]
    const currentIsObject =
      typeof current === 'object' && current !== null && !Array.isArray(current)
    const incomingIsObject =
      typeof incoming === 'object' && incoming !== null && !Array.isArray(incoming)
    if (
      currentIsObject &&
      incomingIsObject
    ) {
      result[key] = mergeDefaults(current as Record<string, unknown>, incoming as Record<string, unknown>)
    } else if (currentIsObject && !incomingIsObject) {
      return
    } else {
      result[key] = incoming
    }
  })
  return result as T
}

export default function LegalEntitiesPage() {
  const navigate = useNavigate()
  const [entities, setEntities] = useState<LegalEntity[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const selectedEntity = useMemo(
    () => entities.find((entity) => entity.id === selectedId) || null,
    [entities, selectedId]
  )

  const methods = useForm<LegalEntityFormData>({
    resolver: zodResolver(legalEntitySchema),
    defaultValues: emptyEntity,
    mode: 'onBlur',
  })

  const { handleSubmit, reset, formState, setValue, getValues } = methods

  // Charger les entités au montage
  useEffect(() => {
    const loadEntities = async () => {
      try {
        setIsLoading(true)
        const data = await getSergeLegalEntities()
        const list = Array.isArray(data) ? data : (data as { 'hydra:member'?: LegalEntity[] })['hydra:member'] || []
        setEntities(list)
        if (list.length > 0) {
          setSelectedId(list[0].id)
          setCurrentStep(STEPS.length - 1)
        }
      } catch {
        toast.error('Erreur lors du chargement des entités légales')
      } finally {
        setIsLoading(false)
      }
    }

    loadEntities()
  }, [])

  // Charger l'entité sélectionnée dans le formulaire
  useEffect(() => {
    if (selectedEntity) {
      const normalized = normalizeNulls(selectedEntity) as LegalEntityFormData
      const merged = mergeDefaults(emptyEntity, normalized)
      reset(merged)
    } else if (!selectedId) {
      reset(emptyEntity)
    }
  }, [selectedEntity, selectedId, reset])

  const handleCreateNew = () => {
    setSelectedId(null)
    reset(emptyEntity)
    setCurrentStep(0)
  }

  const handleSave = async (data: LegalEntityFormData) => {
    try {
      setIsSaving(true)
      let saved: LegalEntity

      if (selectedId) {
        saved = await updateSergeLegalEntity(selectedId, data)
      } else {
        saved = await createSergeLegalEntity(data)
      }

      const response = await getSergeLegalEntities()
      const list = Array.isArray(response) ? response : (response as { 'hydra:member'?: LegalEntity[] })['hydra:member'] || []
      setEntities(list)
      setSelectedId(saved.id)
      setCurrentStep(STEPS.length - 1)
      toast.success('Entité légale enregistrée')
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement")
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const handleInvalid = (errors: FieldErrors<LegalEntityFormData>) => {
    const stepIndex = findFirstErrorStep(errors)
    if (stepIndex !== null && stepIndex !== currentStep) {
      setCurrentStep(stepIndex)
    }
    const detail = findFirstErrorDetail(errors)
    if (detail?.message) {
      toast.error(`${detail.path}: ${detail.message}`)
    } else if (detail?.path) {
      toast.error(`Erreur dans le champ: ${detail.path}`)
    } else {
      toast.error('Veuillez corriger les erreurs du formulaire')
    }
  }

  const handleDelete = async () => {
    if (!selectedEntity) return
    if (!confirm(`Supprimer l'entité "${selectedEntity.name}" ?`)) {
      return
    }

    try {
      setIsDeleting(true)
      await deleteSergeLegalEntity(selectedEntity.id)
      const response = await getSergeLegalEntities()
      const list = Array.isArray(response) ? response : (response as { 'hydra:member'?: LegalEntity[] })['hydra:member'] || []
      setEntities(list)
      setSelectedId(list[0]?.id ?? null)
      setCurrentStep(0)
      toast.success('Entité supprimée')
    } catch {
      toast.error('Erreur lors de la suppression')
    } finally {
      setIsDeleting(false)
    }
  }

  const canGoNext = () => {
    // Pour l'étape 0 (Général), vérifier que le nom est rempli
    if (currentStep === 0) {
      const name = methods.watch('name')
      return name && name.trim().length > 0
    }
    // Les autres étapes sont optionnelles
    return true
  }

  const handleNext = async () => {
    if (currentStep === 4) {
      const values = getValues()
      if (values.softwareUsed?.length) {
        const cleaned = values.softwareUsed.filter((item) => {
          if (!item) return false
          const hasText =
            (item.name && item.name.trim()) ||
            (item.version && item.version.trim()) ||
            (item.license && item.license.trim()) ||
            (item.url && item.url.trim())
          return Boolean(hasText)
        })
        if (cleaned.length !== values.softwareUsed.length) {
          setValue('softwareUsed', cleaned, { shouldDirty: true })
        }
      }
      if (values.paymentSchedule?.length) {
        const cleaned = values.paymentSchedule.filter((item) => {
          if (!item) return false
          const hasText =
            (item.label && item.label.trim()) ||
            (item.dueDate && item.dueDate.trim()) ||
            (item.trigger && item.trigger.trim())
          const hasNumbers = item.amount !== undefined || item.percentage !== undefined
          return Boolean(hasText || hasNumbers)
        })
        if (cleaned.length !== values.paymentSchedule.length) {
          setValue('paymentSchedule', cleaned, { shouldDirty: true })
        }
      }
    }

    const fieldsToValidate = STEP_FIELDS[currentStep] ?? []
    const isValid = fieldsToValidate.length
      ? await methods.trigger(fieldsToValidate, { shouldFocus: true })
      : await methods.trigger(undefined, { shouldFocus: true })
    if (isValid && currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1)
    } else if (!isValid) {
      const detail = findFirstErrorDetail(methods.formState.errors)
      if (detail?.message) {
        toast.error(`${detail.path}: ${detail.message}`)
      } else if (detail?.path) {
        toast.error(`Erreur dans le champ: ${detail.path}`)
      } else {
        toast.error('Veuillez corriger les erreurs du formulaire')
      }
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <GeneralInfoStep />
      case 1:
        return <ContactStep />
      case 2:
        return <RGPDStep />
      case 3:
        return <CGVStep />
      case 4:
        return <SoftwareStep />
      case 5:
        return (
          <div className="space-y-6">
            <GeneralInfoStep />
            <ContactStep />
            <RGPDStep />
            <CGVStep />
            <SoftwareStep />
          </div>
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg p-6">
      <div className="max-w-none mx-0 grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <section className="col-span-12 lg:col-span-3 space-y-4">
          <div className="border border-border bg-surface p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-mono text-foreground-muted uppercase tracking-[0.35em]">
                  Entités légales
                </p>
                <h1 className="text-2xl font-bold text-foreground mt-2">Vos entités</h1>
              </div>
              <Button variant="outline" size="sm" className="gap-2" onClick={handleCreateNew}>
                <Plus className="w-4 h-4" />
                Nouveau
              </Button>
            </div>
          </div>

          <div className="border border-border bg-surface p-4 space-y-2">
            {entities.length === 0 ? (
              <div className="text-sm text-foreground-secondary">Aucune entité pour l'instant.</div>
            ) : (
              entities.map((entity) => (
                <button
                  key={entity.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(entity.id)
                    setCurrentStep(STEPS.length - 1)
                  }}
                  className={`w-full text-left border border-border p-3 text-sm transition-colors ${
                    entity.id === selectedId
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'text-foreground hover:border-primary/60 hover:bg-surface-hover'
                  }`}
                >
                  <div className="font-semibold">{entity.name}</div>
                  {entity.legalName && (
                    <div className="text-xs text-foreground-muted mt-1">{entity.legalName}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </section>

        {/* Main form */}
        <section className="col-span-12 lg:col-span-9 space-y-4">
          <FormProvider {...methods}>
            <div className="border border-border bg-surface p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {selectedId ? 'Éditer une entité' : 'Nouvelle entité'}
                  </h2>
                  <p className="text-sm text-foreground-secondary mt-2">
                    Remplissez les informations légales qui alimenteront la génération des documents.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate('/dashboard/projets')}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour aux projets
                  </Button>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit(handleSave, handleInvalid)}>
              {/* Progress */}
              <div className="border border-border bg-surface p-6 mb-4">
                <WizardProgress steps={STEPS} currentStep={currentStep} />
              </div>

              {/* Step content */}
              <div className="min-h-[500px]">{renderStep()}</div>

              {/* Navigation */}
              <div className="border border-border bg-surface p-5 mt-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {currentStep > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="md"
                        onClick={handlePrevious}
                        className="gap-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Précédent
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {selectedEntity && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        onClick={handleDelete}
                        className="text-red-400 hover:text-red-300"
                        disabled={isDeleting}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    )}

                    {currentStep < STEPS.length - 1 ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={handleNext}
                        disabled={!canGoNext()}
                        className="gap-2"
                      >
                        Suivant
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={isSaving}
                        className="gap-2"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Enregistrement...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Enregistrer
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Step info */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-foreground-muted font-mono">
                    <div>
                      Étape {currentStep + 1} / {STEPS.length}
                    </div>
                    <div>
                      {formState.isDirty && !selectedId && (
                        <span className="text-amber-400">Modifications non sauvegardées</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </section>
      </div>
    </div>
  )
}
